import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Gamepad2, Users, Sparkles, Gift, Rocket,
  TrendingUp, Award, Clock, Play, RefreshCw,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle,
  Trash2, UserPlus, ArrowLeft, Eye, Package,
  Target, Flame, Heart, Sun, Zap, RotateCcw
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts'
import { gameAPI } from '../api/client'

// === Stat Card Component ===
function StatCard({ icon: Icon, value, label, sublabel, color = 'blue', trend }) {
  const colors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
    pink: 'text-pink-400',
    cyan: 'text-cyan-400'
  }

  return (
    <div className="glass p-5 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-6 h-6 ${colors[color]}`} />
        {trend && (
          <span className={`text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-white mb-1">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-white/60">{label}</div>
      {sublabel && <div className="text-xs text-white/40 mt-1">{sublabel}</div>}
    </div>
  )
}

// === Players Table (Clickable) ===
function PlayersTable({ players, isLoading, onSelectPlayer }) {
  if (isLoading) {
    return <div className="text-white/60 text-center py-8">Загрузка...</div>
  }

  if (!players?.length) {
    return <div className="text-white/60 text-center py-8">Нет данных</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-white/60 text-sm border-b border-white/10">
            <th className="text-left py-3 px-4">#</th>
            <th className="text-left py-3 px-4">Игрок</th>
            <th className="text-right py-3 px-4">Уровень</th>
            <th className="text-right py-3 px-4">XP</th>
            <th className="text-right py-3 px-4">Эмоции</th>
            <th className="text-right py-3 px-4">Подарки</th>
            <th className="text-center py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, idx) => (
            <tr
              key={player.user_id}
              className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
              onClick={() => onSelectPlayer(player.user_id)}
            >
              <td className="py-3 px-4">
                <span className={`
                  inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                  ${idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                    idx === 2 ? 'bg-orange-500/20 text-orange-400' :
                    'text-white/40'}
                `}>
                  {idx + 1}
                </span>
              </td>
              <td className="py-3 px-4">
                <div className="text-white font-medium">
                  {player.display_name || player.login}
                </div>
                <div className="text-white/40 text-xs">ID: {player.user_id}</div>
              </td>
              <td className="py-3 px-4 text-right">
                <span className="text-purple-400 font-bold">{player.main_sunshine_level || player.level}</span>
              </td>
              <td className="py-3 px-4 text-right text-white/80">
                {(player.main_sunshine_xp || player.total_xp || 0).toLocaleString()}
              </td>
              <td className="py-3 px-4 text-right text-white/80">
                {(player.total_emotions || player.emotions_produced || 0).toLocaleString()}
              </td>
              <td className="py-3 px-4 text-right text-white/80">
                {(player.gifts_sent || 0).toLocaleString()}
              </td>
              <td className="py-3 px-4 text-center">
                <Eye className="w-4 h-4 text-white/30 inline" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// === Player Detail Panel ===
function PlayerDetailPanel({ userId, onBack }) {
  const queryClient = useQueryClient()

  const { data: player, isLoading, error, refetch } = useQuery(
    ['playerDetail', userId],
    () => gameAPI.getUserDetail(userId),
    { enabled: !!userId }
  )

  const resetMutation = useMutation(
    () => gameAPI.resetPlayer(userId),
    {
      onSuccess: () => {
        refetch()
        queryClient.invalidateQueries('gameStats')
        queryClient.invalidateQueries('gameUsers')
      }
    }
  )

  const initMutation = useMutation(
    () => gameAPI.initPlayer(userId),
    {
      onSuccess: () => {
        refetch()
      }
    }
  )

  const [showResetConfirm, setShowResetConfirm] = useState(false)

  if (isLoading) return <div className="text-white/60 text-center py-8">Загрузка данных игрока...</div>
  if (error) return <div className="text-red-400 text-center py-8">Ошибка: {error.message}</div>
  if (!player) return null

  const { user, sunshines, emotion_slots, inventory, quests, gifts, settings, streak } = player
  const mainSunshine = sunshines?.find(s => s.slot_index === 0)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-white">{user.display_name || user.login}</h2>
            <div className="text-white/50 text-sm">ID: {user.user_id} | TG: {user.telegram_id || '—'}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => initMutation.mutate()}
            disabled={initMutation.isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 text-sm disabled:opacity-50"
          >
            <UserPlus className="w-4 h-4" />
            {initMutation.isLoading ? 'Инициализация...' : 'Инициализировать'}
          </button>
          {!showResetConfirm ? (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm"
            >
              <RotateCcw className="w-4 h-4" />
              Сбросить
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { resetMutation.mutate(); setShowResetConfirm(false) }}
                disabled={resetMutation.isLoading}
                className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {resetMutation.isLoading ? 'Сброс...' : 'Да, сбросить'}
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-3 py-2 rounded-lg bg-white/10 text-white/60 text-sm hover:bg-white/20"
              >
                Отмена
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mutation results */}
      {initMutation.isSuccess && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm">
          Инициализация выполнена: {JSON.stringify(initMutation.data?.results, null, 0)}
        </div>
      )}
      {resetMutation.isSuccess && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-400 text-sm">
          Данные сброшены. Удалено: {JSON.stringify(resetMutation.data?.deleted, null, 0)}
        </div>
      )}

      {/* Sunshine */}
      <div className="glass p-4 rounded-xl">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Sun className="w-5 h-5 text-yellow-400" />
          Солнышко
        </h3>
        {mainSunshine ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InfoBlock label="Уровень" value={mainSunshine.level} color="purple" />
            <InfoBlock label="XP" value={`${mainSunshine.xp || 0} / ${mainSunshine.xp_to_next_level || 100}`} />
            <InfoBlock label="Всего XP" value={mainSunshine.total_xp || 0} />
            <InfoBlock label="Ян" value={`${Math.round(mainSunshine.yang_current)} / ${Math.round(mainSunshine.yang_max)}`} color="yellow" />
            <InfoBlock label="Инь" value={`${Math.round(mainSunshine.yin_current)} / ${Math.round(mainSunshine.yin_max)}`} color="blue" />
            <InfoBlock label="Эмоции произведено" value={mainSunshine.emotions_produced} color="green" />
            <InfoBlock label="Подарки отправлены" value={mainSunshine.gifts_sent} color="pink" />
            <InfoBlock label="Подарки получены" value={mainSunshine.gifts_received} color="cyan" />
          </div>
        ) : (
          <div className="text-white/50 text-sm">Солнышко не создано</div>
        )}
      </div>

      {/* Emotion Slots */}
      <div className="glass p-4 rounded-xl">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-400" />
          Слоты производства ({emotion_slots?.length || 0})
        </h3>
        {emotion_slots?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {emotion_slots.map(slot => (
              <div key={slot.id} className="bg-white/5 rounded-lg p-3">
                <div className="text-white font-medium text-sm">{slot.emotion_icon} {slot.emotion_name_ru}</div>
                <div className="text-white/50 text-xs mt-1">
                  Уровень: {slot.level} | {slot.is_unlocked ? '🔓' : '🔒'} {slot.is_frozen ? '❄️' : ''}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/50 text-sm">Нет слотов</div>
        )}
      </div>

      {/* Inventory */}
      <div className="glass p-4 rounded-xl">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Package className="w-5 h-5 text-green-400" />
          Инвентарь ({inventory?.length || 0})
        </h3>
        {inventory?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {inventory.map(item => (
              <div key={item.emotion_id} className="bg-white/5 rounded-lg p-3">
                <div className="text-white font-medium text-sm">{item.icon} {item.name_ru}</div>
                <div className="text-white/50 text-xs mt-1">
                  В сумке: {item.amount} | Произведено: {item.total_produced} | Подарено: {item.total_gifted}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/50 text-sm">Инвентарь пуст</div>
        )}
      </div>

      {/* Quests */}
      <div className="glass p-4 rounded-xl">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-400" />
          Квесты ({quests?.length || 0})
        </h3>
        {quests?.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {quests.map(q => (
              <div key={q.id} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="text-white text-sm font-medium">{q.title_ru || q.quest_id}</div>
                  <div className="text-white/50 text-xs">
                    {q.quest_type} | {q.current_progress}/{q.target_progress} | +{q.reward_xp} XP
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  q.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  q.status === 'claimed' ? 'bg-blue-500/20 text-blue-400' :
                  q.status === 'active' ? 'bg-yellow-500/20 text-yellow-400' :
                  q.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                  'bg-white/10 text-white/50'
                }`}>
                  {q.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white/50 text-sm">Нет квестов</div>
        )}
      </div>

      {/* Streak & Settings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass p-4 rounded-xl">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" />
            Серия входов
          </h3>
          {streak ? (
            <div className="grid grid-cols-2 gap-2">
              <InfoBlock label="Текущая серия" value={streak.current_streak} color="orange" />
              <InfoBlock label="Рекорд" value={streak.longest_streak} color="yellow" />
              <InfoBlock label="Всего входов" value={streak.total_logins} />
              <InfoBlock label="Последний вход" value={streak.last_login_date?.split('T')[0] || '—'} />
            </div>
          ) : (
            <div className="text-white/50 text-sm">Нет данных о стрике</div>
          )}
        </div>

        <div className="glass p-4 rounded-xl">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" />
            Подарки (последние {gifts?.length || 0})
          </h3>
          {gifts?.length > 0 ? (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {gifts.map(g => (
                <div key={g.id} className="text-white/60 text-xs flex justify-between">
                  <span>{g.receiver_type} | {g.total_emotions} эмоций</span>
                  <span>+{g.xp_earned} XP</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-white/50 text-sm">Нет подарков</div>
          )}
        </div>
      </div>
    </div>
  )
}

function InfoBlock({ label, value, color = 'white' }) {
  const colorClass = {
    white: 'text-white',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
    green: 'text-green-400',
    pink: 'text-pink-400',
    cyan: 'text-cyan-400',
    orange: 'text-orange-400',
  }[color] || 'text-white'

  return (
    <div className="bg-white/5 rounded p-2">
      <div className="text-white/40 text-xs">{label}</div>
      <div className={`${colorClass} text-sm font-medium`}>{value}</div>
    </div>
  )
}

// === Cron Tasks Panel ===
function CronTasksPanel({ cronStatus, onRunTask }) {
  const [expanded, setExpanded] = useState(true)
  const [expandedTask, setExpandedTask] = useState(null)

  const statusColors = {
    idle: 'text-gray-400',
    running: 'text-blue-400',
    error: 'text-red-400',
    success: 'text-green-400'
  }

  const statusBadgeColors = {
    idle: 'bg-gray-500/20 text-gray-400',
    running: 'bg-blue-500/20 text-blue-400',
    error: 'bg-red-500/20 text-red-400',
    success: 'bg-green-500/20 text-green-400'
  }

  const statusIcons = {
    idle: Clock,
    running: RefreshCw,
    error: AlertCircle,
    success: CheckCircle
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '-'
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  }

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'никогда'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = Math.floor((now - date) / 1000)

    if (diff < 60) return `${diff} сек назад`
    if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`
    return date.toLocaleString('ru')
  }

  const formatNextRun = (dateStr) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = Math.floor((date - now) / 1000)

    if (diff < 0) return 'сейчас'
    if (diff < 60) return `через ${diff} сек`
    if (diff < 3600) return `через ${Math.floor(diff / 60)} мин`
    if (diff < 86400) return `через ${Math.floor(diff / 3600)} ч`
    return date.toLocaleString('ru')
  }

  return (
    <div className="glass rounded-xl">
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-400" />
          Cron-задачи
          {cronStatus?.server_time && (
            <span className="text-xs text-white/40 font-normal ml-2">
              UTC: {new Date(cronStatus.server_time).toLocaleTimeString('ru')}
            </span>
          )}
        </h3>
        {expanded ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {cronStatus?.tasks?.map((task) => {
            const StatusIcon = statusIcons[task.status] || Clock
            const isExpanded = expandedTask === task.name
            return (
              <div
                key={task.name}
                className="bg-white/5 rounded-lg overflow-hidden"
              >
                {/* Task Header */}
                <div className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <StatusIcon className={`w-4 h-4 flex-shrink-0 ${statusColors[task.status]} ${task.status === 'running' ? 'animate-spin' : ''}`} />
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate">{task.name}</div>
                      <div className="text-white/50 text-xs truncate">{task.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Stats badges */}
                    <div className="hidden sm:flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">
                        {task.run_count || 0} запусков
                      </span>
                      {task.error_count > 0 && (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                          {task.error_count} ошибок
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedTask(isExpanded ? null : task.name)
                      }}
                      className="p-1 rounded text-white/40 hover:text-white/80"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onRunTask(task.name)
                      }}
                      disabled={task.status === 'running'}
                      className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Task Details (expanded) */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 space-y-3 border-t border-white/5">
                    {/* Schedule & Timing */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3">
                      <div className="bg-white/5 rounded p-2">
                        <div className="text-white/40 text-xs">Расписание</div>
                        <div className="text-white text-sm font-medium">{task.interval}</div>
                      </div>
                      <div className="bg-white/5 rounded p-2">
                        <div className="text-white/40 text-xs">Последний запуск</div>
                        <div className="text-white text-sm font-medium">{formatTimeAgo(task.last_run)}</div>
                      </div>
                      <div className="bg-white/5 rounded p-2">
                        <div className="text-white/40 text-xs">Следующий запуск</div>
                        <div className="text-cyan-400 text-sm font-medium">{formatNextRun(task.next_run)}</div>
                      </div>
                      <div className="bg-white/5 rounded p-2">
                        <div className="text-white/40 text-xs">Длительность</div>
                        <div className="text-white text-sm font-medium">{formatDuration(task.last_duration_seconds)}</div>
                      </div>
                    </div>

                    {/* Last Result */}
                    {task.last_result && (
                      <div className="bg-white/5 rounded p-2">
                        <div className="text-white/40 text-xs mb-1">Последний результат</div>
                        <div className="text-green-400 text-sm">{task.last_result}</div>
                      </div>
                    )}

                    {/* History */}
                    {task.history && task.history.length > 0 && (
                      <div>
                        <div className="text-white/40 text-xs mb-2">История запусков</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {task.history.slice().reverse().map((run, idx) => {
                            const RunIcon = statusIcons[run.status] || Clock
                            return (
                              <div key={idx} className="flex items-center gap-2 text-xs bg-white/5 rounded p-2">
                                <RunIcon className={`w-3 h-3 ${statusColors[run.status]}`} />
                                <span className="text-white/60">
                                  {new Date(run.started_at).toLocaleString('ru')}
                                </span>
                                <span className={statusBadgeColors[run.status] + ' px-1.5 py-0.5 rounded'}>
                                  {run.status}
                                </span>
                                {run.duration_seconds && (
                                  <span className="text-white/40">{formatDuration(run.duration_seconds)}</span>
                                )}
                                {run.affected_rows !== null && run.affected_rows !== undefined && (
                                  <span className="text-white/40">({run.affected_rows} записей)</span>
                                )}
                                <span className="text-white/60 truncate flex-1">{run.result}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {(!cronStatus?.tasks || cronStatus.tasks.length === 0) && (
            <div className="text-white/50 text-center py-4">Нет задач</div>
          )}
        </div>
      )}
    </div>
  )
}

// === Level Distribution Chart ===
function LevelDistributionChart({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return <div className="text-white/60 text-center py-8">Нет данных</div>
  }

  const chartData = Object.entries(data)
    .map(([level, count]) => ({ level: parseInt(level), count }))
    .sort((a, b) => a.level - b.level)

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis dataKey="level" stroke="rgba(255,255,255,0.5)" fontSize={12} />
        <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            color: 'white'
          }}
        />
        <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// === Activity Chart ===
function ActivityChart({ data, isLoading }) {
  if (isLoading) {
    return <div className="text-white/60 text-center py-8">Загрузка...</div>
  }

  if (!data?.length) {
    return <div className="text-white/60 text-center py-8">Нет данных</div>
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis
          dataKey="date"
          stroke="rgba(255,255,255,0.5)"
          fontSize={12}
          tickFormatter={(val) => val ? new Date(val).toLocaleDateString('ru', { day: 'numeric', month: 'short' }) : ''}
        />
        <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            color: 'white'
          }}
        />
        <Line
          type="monotone"
          dataKey="active_users"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          name="Активные игроки"
        />
        <Line
          type="monotone"
          dataKey="emotions"
          stroke="#10b981"
          strokeWidth={2}
          dot={false}
          name="Эмоции"
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// === Main Component ===
export default function GameMonitor() {
  const queryClient = useQueryClient()
  const [topCategory, setTopCategory] = useState('level')
  const [selectedPlayerId, setSelectedPlayerId] = useState(null)
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false)
  const [activeTab, setActiveTab] = useState('overview') // overview | players

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery(
    'gameStats',
    gameAPI.getStats,
    { refetchInterval: 30000 }
  )

  const { data: topPlayers, isLoading: topLoading } = useQuery(
    ['gameTop', topCategory],
    () => gameAPI.getTop(topCategory),
    { refetchInterval: 60000 }
  )

  const { data: allUsers, isLoading: usersLoading } = useQuery(
    'gameUsers',
    () => gameAPI.getUsers({ limit: 100, order_by: 'level' }),
    { refetchInterval: 60000 }
  )

  const { data: questStats } = useQuery(
    'gameQuestStats',
    gameAPI.getQuestStats,
    { refetchInterval: 60000 }
  )

  const { data: cronStatus, refetch: refetchCron } = useQuery(
    'gameCronStatus',
    gameAPI.getCronStatus,
    { refetchInterval: 10000 }
  )

  const { data: activityChart, isLoading: activityLoading } = useQuery(
    'gameActivityChart',
    () => gameAPI.getActivityChart(7),
    { refetchInterval: 300000 }
  )

  // Mutations
  const runTaskMutation = useMutation(
    (taskName) => gameAPI.runCronTask(taskName),
    {
      onSuccess: () => {
        refetchCron()
      }
    }
  )

  const resetAllMutation = useMutation(
    () => gameAPI.resetAllPlayers(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('gameStats')
        queryClient.invalidateQueries('gameUsers')
        queryClient.invalidateQueries('gameTop')
        setShowResetAllConfirm(false)
      }
    }
  )

  // If player is selected, show detail view
  if (selectedPlayerId) {
    return (
      <div className="space-y-6">
        <PlayerDetailPanel
          userId={selectedPlayerId}
          onBack={() => setSelectedPlayerId(null)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Gamepad2 className="w-7 h-7 text-purple-400" />
          Игра «Путь Солнышка»
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => queryClient.invalidateQueries()}
            className="btn-ghost flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Обновить
          </button>
          {!showResetAllConfirm ? (
            <button
              onClick={() => setShowResetAllConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Сбросить всех
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-red-400 text-sm">Удалить ВСЕ данные?</span>
              <button
                onClick={() => resetAllMutation.mutate()}
                disabled={resetAllMutation.isLoading}
                className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {resetAllMutation.isLoading ? 'Сброс...' : 'Да, удалить'}
              </button>
              <button
                onClick={() => setShowResetAllConfirm(false)}
                className="px-3 py-2 rounded-lg bg-white/10 text-white/60 text-sm hover:bg-white/20"
              >
                Отмена
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reset result */}
      {resetAllMutation.isSuccess && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-400 text-sm">
          Все данные сброшены. Удалено записей: {resetAllMutation.data?.total_records_cleared}
        </div>
      )}
      {resetAllMutation.isError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          Ошибка: {resetAllMutation.error?.response?.data?.detail || resetAllMutation.error?.message}
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg text-sm transition ${
            activeTab === 'overview'
              ? 'bg-purple-500/30 text-purple-300'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
        >
          Обзор
        </button>
        <button
          onClick={() => setActiveTab('players')}
          className={`px-4 py-2 rounded-lg text-sm transition ${
            activeTab === 'players'
              ? 'bg-purple-500/30 text-purple-300'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
        >
          Игроки ({allUsers?.length || 0})
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              value={stats?.users_with_sunshine || 0}
              label="Игроков"
              sublabel={`${stats?.active_today || 0} сегодня`}
              color="blue"
            />
            <StatCard
              icon={Sparkles}
              value={stats?.total_emotions_produced || 0}
              label="Эмоций создано"
              color="purple"
            />
            <StatCard
              icon={Gift}
              value={stats?.total_gifts_sent || 0}
              label="Подарков отправлено"
              color="pink"
            />
            <StatCard
              icon={Rocket}
              value={stats?.total_expeditions || 0}
              label="Экспедиций"
              color="cyan"
            />
          </div>

          {/* Second Row Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={TrendingUp}
              value={stats?.avg_level?.toFixed(1) || '1.0'}
              label="Средний уровень"
              sublabel={`Макс: ${stats?.max_level || 1}`}
              color="green"
            />
            <StatCard
              icon={Award}
              value={questStats?.completed_today || 0}
              label="Заданий выполнено сегодня"
              sublabel={`${questStats?.completion_rate || 0}% завершения`}
              color="yellow"
            />
            <StatCard
              icon={Users}
              value={stats?.active_week || 0}
              label="Активных за неделю"
              color="blue"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Activity Chart */}
            <div className="glass p-5 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                А��тивность за 7 дней
              </h3>
              <ActivityChart data={activityChart} isLoading={activityLoading} />
            </div>

            {/* Level Distribution */}
            <div className="glass p-5 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-400" />
                Распределение по уровням
              </h3>
              <LevelDistributionChart data={stats?.level_distribution} />
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Players */}
            <div className="lg:col-span-2 glass p-5 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Топ игроков
                </h3>
                <div className="flex gap-2">
                  {['level', 'xp', 'emotions'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setTopCategory(cat)}
                      className={`px-3 py-1 rounded-lg text-sm transition ${
                        topCategory === cat
                          ? 'bg-purple-500/30 text-purple-300'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {cat === 'level' ? 'Уровень' : cat === 'xp' ? 'XP' : 'Эмоции'}
                    </button>
                  ))}
                </div>
              </div>
              <PlayersTable
                players={topPlayers}
                isLoading={topLoading}
                onSelectPlayer={setSelectedPlayerId}
              />
            </div>

            {/* Cron Tasks */}
            <div>
              <CronTasksPanel
                cronStatus={cronStatus}
                onRunTask={(name) => runTaskMutation.mutate(name)}
              />
            </div>
          </div>
        </>
      )}

      {activeTab === 'players' && (
        <div className="glass p-5 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Все игроки
            </h3>
          </div>
          <PlayersTable
            players={allUsers}
            isLoading={usersLoading}
            onSelectPlayer={setSelectedPlayerId}
          />
        </div>
      )}
    </div>
  )
}
